/**
 * ClearNumberConfirm.tsx — the destructive-clear confirmation step (N2 AC7).
 *
 * Visually distinct from `NumberEditForm`'s routine gold review-confirm: a
 * red ring/button (`status-down`, not `gold`), matching the wireframe's rule
 * that a destroy action must never look like a routine save. No bare toggle
 * ever performs the clear — this panel is the only path to
 * `PUT /api/config/numbers` with `phone_number_id: null` (D6: clearing is
 * not a special case, just a null value on the same write route).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UnauthorizedError, type ApiClient } from '../../api/client';
import floating from '../../lib/floating';

export interface ClearNumberConfirmProps {
  apiClient: ApiClient;
  phoneNumberId: string;
  tenantName: string;
  onCleared: () => Promise<void>;
  onCancel: () => void;
}

type ClearError = 'unauthorized' | 'unavailable';

export const ClearNumberConfirm = ({
  apiClient,
  phoneNumberId,
  tenantName,
  onCleared,
  onCancel,
}: ClearNumberConfirmProps) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ClearError | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus moves to the panel on open (WCAG 2.4.3), same as NumberEditForm's
  // review-confirm and LeadTransitionForm.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const handleClear = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.setNumberConfig({ phone_number_id: null });
      await onCleared();
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setError('unauthorized');
      } else {
        setError('unavailable');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      role="group"
      aria-label={t('numbers.clearConfirm.heading')}
      data-testid="number-clear-confirm"
      className="flex flex-col gap-3 rounded-lg bg-surface-1 p-4 ring-2 ring-inset ring-status-down/60 focus:outline-none focus:ring-2 focus:ring-status-down"
    >
      <h2 className="text-h3 text-status-down">{t('numbers.clearConfirm.heading')}</h2>
      <p className="text-body text-ink-1">
        {t('numbers.clearConfirm.body', { phoneNumberId, tenant: tenantName })}
      </p>
      <p
        role="note"
        data-testid="number-clear-propagation-notice"
        className="rounded-lg bg-status-degraded/10 px-3 py-2 text-meta text-ink-2 ring-1 ring-inset ring-status-degraded/30"
      >
        {t('numbers.propagationNotice')}
      </p>
      {error != null ? (
        <p
          role="alert"
          data-testid={`number-clear-error-${error}`}
          className="rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40"
        >
          {t(`numbers.clearConfirm.errors.${error}`)}
        </p>
      ) : null}
      <div className="flex gap-3">
        <button
          type="button"
          data-testid="number-clear-confirm-submit"
          disabled={submitting}
          aria-busy={submitting}
          onClick={() => floating(handleClear())}
          className="inline-flex items-center rounded-lg bg-status-down px-4 py-1.5 text-body font-medium text-canvas hover:bg-status-down/90 disabled:opacity-60"
        >
          {submitting ? t('numbers.clearConfirm.clearing') : t('numbers.clearConfirm.confirm')}
        </button>
        <button
          type="button"
          data-testid="number-clear-cancel"
          disabled={submitting}
          onClick={onCancel}
          className="inline-flex items-center rounded-lg bg-surface-2 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3 disabled:opacity-60"
        >
          {t('numbers.clearConfirm.cancel')}
        </button>
      </div>
    </div>
  );
};
